import React, { useMemo, useState } from 'react';
import { Printer, Scissors } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { cutsByColor, CutOrder, CutRef, CutRow, NO_COLOR } from './cutByColor';
import { TableCard, Table, THead, Th, Tr, Td, cn } from './ui/Table';
import { Button } from './ui/arc/button';
import { Badge } from './ui/arc/badge';
import { Checkbox } from './ui/arc/checkbox';
import { MetricCard } from './ui/arc/metric-card';
import { EmptyState } from './ui/arc/empty-state';
import { OrderPeek } from './OrderPeek';

// Vista "Cortes por color" del Taller (01/11/2026): las piezas que faltan cortar, por color de
// acrílico → modelo → medida, para armar el archivo de corte de cada plancha. Desde acá se marca CORTADO (fila entera o un pedido); el
// las piezas salen de la lista y, si era lo último del pedido, el pedido pasa a "cortado". Lógica en cutByColor.ts.

export function CortesPorColor({ orders, loading, onCut }: {
  orders: CutOrder[]; loading: boolean;
  /** Marca como cortadas esas piezas (lo hace Taller: guarda y actualiza la cola). */
  onCut?: (refs: CutRef[]) => Promise<boolean>;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [peek, setPeek] = useState<number | null>(null); // pedido abierto en popup
  // Marcar cortado: toda la fila (todos sus pedidos) o un pedido solo. Confirma porque saca las
  // piezas de la lista y, si era lo último del pedido, el pedido pasa a "cortado".
  const markCut = async (key: string, r: CutRow, color: string, only?: CutRow['orders'][number]) => {
    if (!onCut) return;
    const targets = only ? [only] : r.orders;
    const qty = targets.reduce((n, o) => n + o.qty, 0);
    const what = `${qty} × ${r.model}${r.size ? ` ${r.size}` : ''} (${color})`;
    if (!window.confirm(`¿Marcar como CORTADO ${what}?\n\nPedidos: ${targets.map(o => o.num).join(', ')}`)) return;
    setBusy(key);
    await onCut(targets.flatMap(o => o.refs));
    setBusy(null);
  };
  const [includeUnpaid, setIncludeUnpaid] = useState(true);
  const colors = useMemo(() => cutsByColor(orders, { includeUnpaid }), [orders, includeUnpaid]);
  const total = colors.reduce((s, c) => s + c.total, 0);
  const setRows = colors.reduce((s, c) => s + c.rows.filter(r => r.fromSet).length, 0);
  const noColor = colors.find(c => c.color === NO_COLOR)?.total || 0;
  const nOrders = new Set(colors.flatMap(c => c.rows.flatMap(r => r.orders.map(o => o.num)))).size;

  const ordersText = (os: { num: string; qty: number; unpaid: boolean }[]) =>
    os.map(o => `${o.num}${o.qty > 1 ? ` (×${o.qty})` : ''}${o.unpaid ? '*' : ''}`).join(', ');

  const printPdf = () => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const fecha = new Date().toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    doc.setFont('helvetica', 'bold'); doc.setFontSize(15); doc.text('Cortes por color — estantes', 12, 15);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(110);
    doc.text(`${fecha} · ${total} piezas · ${nOrders} pedidos${includeUnpaid ? ' · * = sin pagar' : ' · sin los impagos'}`, 12, 21);
    doc.setTextColor(0);
    let y = 26;
    for (const c of colors) {
      autoTable(doc, {
        startY: y,
        head: [[{ content: `${c.color} — ${c.total}`, colSpan: 4 }], ['Modelo', 'Medida', 'Cant.', 'Pedidos']],
        body: c.rows.map(r => [r.model + (r.fromSet ? ' (de set)' : ''), r.size || '—', String(r.qty), ordersText(r.orders)]),
        styles: { fontSize: 8.5, cellPadding: 1.6, lineColor: 200, lineWidth: 0.1 },
        headStyles: { fillColor: [40, 40, 40], textColor: 255 },
        columnStyles: { 0: { cellWidth: 62 }, 1: { cellWidth: 28 }, 2: { cellWidth: 14, halign: 'center', fontStyle: 'bold' }, 3: { cellWidth: 82 } },
        margin: { left: 12, right: 12 },
      });
      y = (doc as any).lastAutoTable.finalY + 5;
    }
    window.open(URL.createObjectURL(doc.output('blob')), '_blank');
  };

  if (loading) {
    return <div className="p-4">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="mb-3 h-24 animate-pulse rounded-[var(--radius-control)] bg-muted" />)}</div>;
  }

  return (
    <div>
      <section className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-4">
        <MetricCard label="Piezas por cortar" value={total} locale="es-AR" context="Estantes sin el tilde de cortado" />
        <MetricCard label="Colores" value={colors.filter(c => c.color !== NO_COLOR).length} locale="es-AR" context="Una plancha / archivo por color" />
        <MetricCard label="Pedidos" value={nOrders} locale="es-AR" context="Con al menos un estante por cortar" />
        <MetricCard label="Sin medida o color" value={setRows + (noColor ? 1 : 0)} locale="es-AR" context="Sets o pedidos que hay que mirar" />
      </section>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <Checkbox label="Incluir pedidos sin pagar" description="Se marcan con * en la lista" checked={includeUnpaid}
          onCheckedChange={v => setIncludeUnpaid(v === true)} />
        <Button variant="secondary" disabled={!colors.length} onClick={printPdf}><Printer size={16} /> Imprimir / PDF</Button>
      </div>

      {!colors.length ? (
        <EmptyState title="No hay estantes por cortar" description="Todos los estantes de los pedidos abiertos ya tienen el tilde de cortado." />
      ) : (
        <div className="grid gap-4 xl:grid-cols-2">
          {colors.map(c => (
            <TableCard key={c.color}>
              <div className="flex items-center justify-between gap-2 border-b border-line px-4 py-3">
                <div className="flex items-center gap-2">
                  <span className="inline-block h-4 w-4 shrink-0 rounded-full border border-line" style={{ background: c.swatch }} />
                  <span className="text-[length:var(--text-base)] font-semibold text-ink">{c.color}</span>
                </div>
                <Badge tone={c.color === NO_COLOR ? 'warning' : 'neutral'}>{c.total} {c.total === 1 ? 'pieza' : 'piezas'}</Badge>
              </div>
              <Table>
                <THead><tr><Th>Modelo</Th><Th>Medida</Th><Th align="right">Cant.</Th><Th>Pedidos</Th>{onCut && <Th />}</tr></THead>
                <tbody>
                  {c.rows.map(r => (
                    <Tr key={`${r.model}|${r.size}|${r.fromSet}`}>
                      <Td className="font-medium">
                        {r.model}
                        {r.fromSet && <Badge size="sm" tone="warning" className="ml-2" title="El pedido no dice la medida de cada estante del set">de set · ver medida</Badge>}
                      </Td>
                      <Td className={cn('whitespace-nowrap', !r.size && 'text-faint')}>{r.size || '—'}</Td>
                      <Td align="right" className="font-semibold">{r.qty}</Td>
                      <Td className="text-soft">
                        {r.orders.map((o, i) => (
                          <span key={o.num} className="inline-flex items-center whitespace-nowrap">
                            {i > 0 && <span className="mr-1">,</span>}
                            <button type="button" title={`Ver el pedido ${o.num}${o.unpaid ? ' (sin pagar)' : ''}`}
                              className={cn('rounded px-0.5 underline decoration-dotted underline-offset-2 hover:bg-muted hover:text-ink', o.unpaid && 'text-[var(--warning)]')}
                              onClick={() => setPeek(o.orderId)}>
                              {o.num}{o.qty > 1 ? ` (×${o.qty})` : ''}{o.unpaid ? '*' : ''}
                            </button>
                            {onCut && r.orders.length > 1 && (
                              <button type="button" disabled={!!busy} aria-label={`Marcar cortado solo ${o.num}`} title={`Marcar cortado solo ${o.num}`}
                                className="ml-0.5 rounded p-0.5 text-faint hover:bg-muted hover:text-ink disabled:opacity-40"
                                onClick={() => markCut(`${c.color}|${r.model}|${r.size}|${o.num}`, r, c.color, o)}>
                                <Scissors size={12} />
                              </button>
                            )}
                          </span>
                        ))}
                      </Td>
                      {onCut && (
                        <Td align="right">
                          <Button variant="secondary" size="sm" loading={busy === `${c.color}|${r.model}|${r.size}`} disabled={!!busy}
                            title="Marcar cortadas todas las piezas de este renglón" onClick={() => markCut(`${c.color}|${r.model}|${r.size}`, r, c.color)}>
                            <Scissors size={13} /> Cortado
                          </Button>
                        </Td>
                      )}
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </TableCard>
          ))}
        </div>
      )}
      <OrderPeek orderId={peek} onClose={() => setPeek(null)} />
    </div>
  );
}

export default CortesPorColor;
